import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FileText } from "lucide-react";

interface UploadDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierId: string;
  onSuccess: () => void;
}

export const UploadDocumentDialog = ({
  open,
  onOpenChange,
  supplierId,
  onSuccess,
}: UploadDocumentDialogProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setIsUploading(true);

      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${supplierId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('supplier_documents')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('supplier_documents')
        .getPublicUrl(fileName);

      // Save document reference in the database
      const { error: dbError } = await supabase
        .from('supplier_communications')
        .insert({
          supplier_id: supplierId,
          type: 'document',
          content: file.name,
          document_url: publicUrl,
        });

      if (dbError) throw dbError;

      toast.success('Document téléchargé avec succès');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Erreur lors du téléchargement du document');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Télécharger un document</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.xls,.xlsx"
            />
          </div>
          <Button
            onClick={handleUpload}
            disabled={!file || isUploading}
            className="w-full"
          >
            <FileText className="w-4 h-4 mr-2" />
            {isUploading ? 'Téléchargement...' : 'Télécharger'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};